import { Expression } from "../modelling/components/expression_component";
import {
  BooleanTileGrid,
  CategoricalTileGrid,
  NumericTileGrid,
  TileGridJSON,
  fromJSON,
  tileGridFromWorkerParse,
  type WorkerParsedTileGrid,
} from "../modelling/tile_grid";
import { workerPool } from "../../modelling/workerPool";

export interface CompiledDatasetRecord {
    name: string
    id: number
    team_id: number
    gridtype: string
    created_at: string
    updated_at: string
}

export interface DatasetLabelRecord {
    value: number
    label: string
}

export interface TeamDataVectorLayerField {
    name: string
    type?: string
}

export interface TeamDataVectorLayerRecord {
    layer_name: string
    geometry_type?: string
    fields?: TeamDataVectorLayerField[]
    field_values?: Record<string, string[]>
}

export interface TeamDataRecord {
    id: number
    name?: string | null
    display_name: string
    source?: string | null
    description?: string | null
    categorical_raster?: boolean
    categorical_value_count?: number | null
    raster_min_value?: number | null
    raster_max_value?: number | null
    publish_status: string
    geoserver_layer_id?: string | null
    geoserver_url?: string | null
    unique_values?: number[]
    /** Set when a Boolean/Categorical (GeoTIFF) component is linked to this upload. */
    category_labels?: Array<{ value: number; label: string; color?: string }>
    publish_error?: string | null
    vector_layer_name?: string | null
    vector_geometry_type?: string | null
    vector_fields?: TeamDataVectorLayerField[]
    vector_layers?: TeamDataVectorLayerRecord[]
}

function getEmbedShareIdFromLocation(): string | undefined {
    const query = new URLSearchParams(window.location.search)
    const byQuery = query.get('share_id')
    if (byQuery) return byQuery

    const match = window.location.pathname.match(/\/(?:embed|share)\/([^/?#]+)/)
    return match?.[1]
}

function withShareId(url: string): string {
    const shareId = getEmbedShareIdFromLocation()
    if (!shareId) return url
    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}share_id=${encodeURIComponent(shareId)}`
}

function parseJsonOrThrow(request: XMLHttpRequest, context: string): any {
    const text = request.responseText || ''

    try {
        return JSON.parse(text)
    } catch (_e) {
        const snippet = text.slice(0, 120).trim()
        throw new Error(
            `Expected JSON for ${context}, got non-JSON response (status ${request.status})` +
            (snippet ? `: ${snippet}` : '')
        )
    }
}

export function saveModelOutput(name: string, model: TileGridJSON, teamId: number, callback?: (status: number) => void) {
    const formData = new FormData()

    const blob = new Blob([JSON.stringify(model)], { type: "application/json" })

    formData.append('file', blob, 'dataset.json')
    formData.append('name', name)
    formData.append('team_id', teamId.toString())
    formData.append('gridtype', model.type)

    const request = new XMLHttpRequest()
    if(callback) request.addEventListener('load', () => callback(request.status)) 
    request.open('POST', `/teams/${teamId}/datasets`)
    request.setRequestHeader('X-CSRF-Token', (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement).content)
    request.send(formData)

}

export function getDatasets(teamId: number): Promise<Array<CompiledDatasetRecord>> {
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest()
        request.open('GET', withShareId(`/teams/${teamId}/datasets?json=true`))
        request.setRequestHeader('X-CSRF-Token', (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement).content)
        request.onload = () => {
            if (request.status === 200) {
                try {
                    resolve(parseJsonOrThrow(request, 'datasets'))
                } catch (error) {
                    reject(error)
                }
            } else {
                reject(new Error('Failed to fetch datasets'))
            }
        }
        request.onerror = () => {
            reject(new Error('Request error'))
        }
        request.send()
    })
}

export function getTeamData(teamId: number): Promise<Array<TeamDataRecord>> {
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest()
        request.open('GET', withShareId(`/teams/${teamId}/team_data.json`))
        request.setRequestHeader('X-CSRF-Token', (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement).content)
        request.onload = () => {
            if (request.status === 200) {
                try {
                    const response = parseJsonOrThrow(request, 'team data')
                    resolve(response.team_data || [])
                } catch (error) {
                    reject(error)
                }
            } else {
                reject(new Error('Failed to fetch team data'))
            }
        }
        request.onerror = () => {
            reject(new Error('Request error'))
        }
        request.send()
    })
}

/**
 * Falls back to the original main-thread parse path when the worker
 * isn't available (worker pool exhausted permanently, build issue, etc.).
 * Identical behaviour to the pre-worker implementation so failure modes
 * stay predictable.
 */
function parseDatasetOnMainThread(
    responseText: string,
    callback: (
        err: Error | null,
        dataset?: BooleanTileGrid | NumericTileGrid | CategoricalTileGrid | null,
        colorMetadata?: { colors?: any; fill?: any },
    ) => void
) {
    try {
        const response = JSON.parse(responseText)
        const dataset = fromJSON(response)
        const colorMetadata = {
            colors: response.colors,
            fill: response.fill,
        }
        callback(null, dataset, colorMetadata)
    } catch (error) {
        callback(error instanceof Error ? error : new Error(String(error)))
    }
}

export function getDataset(datasetId: number, teamId: number, callback) {
    const request = new XMLHttpRequest()
    request.open('GET', withShareId(`/datasets/${datasetId}?team_id=${teamId}`))
    request.setRequestHeader(
        'X-CSRF-Token',
        (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement).content
    )

    request.onload = function () {
        if (request.status !== 200) {
            callback(new Error('Request failed'))
            return
        }

        // Off-thread parse: `JSON.parse` plus the typed-array build can
        // stall the main thread for hundreds of ms on big saved rasters
        // (the cell data serializes as one JSON entry per cell). The
        // worker pool is already used by the modelling components; piggy-
        // back on it here so the Map view stays responsive while saved
        // datasets are streaming in. On any worker error fall back to the
        // original main-thread path so user-visible behaviour is
        // preserved if the worker bundle is unavailable for any reason.
        const responseText = request.responseText || ''
        workerPool
            .queue((worker: any) => worker.parseSavedDataset(responseText))
            .then((parsed: WorkerParsedTileGrid & { colorMetadata?: { colors?: any; fill?: any } }) => {
                try {
                    const dataset = tileGridFromWorkerParse(parsed)
                    callback(null, dataset, parsed.colorMetadata ?? {})
                } catch (constructError) {
                    callback(
                        constructError instanceof Error
                            ? constructError
                            : new Error(String(constructError))
                    )
                }
            })
            .catch((_workerError: unknown) => {
                // Worker rejected (bundle missing, message decode failed,
                // etc.) — never silently lose the dataset; reparse on the
                // main thread instead.
                parseDatasetOnMainThread(responseText, callback)
            })
    };

    request.onerror = function () {
        callback(new Error('Request error'))
    };

    request.send()
}

export function getDatasetLabels(datasetId: number, teamId: number): Promise<Array<DatasetLabelRecord>> {
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest()
        request.open('GET', withShareId(`/datasets/${datasetId}/labels?team_id=${teamId}`))
        request.setRequestHeader(
            'X-CSRF-Token',
            (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement).content
        )
        request.onload = () => {
            if (request.status === 200) {
                try {
                    const response = parseJsonOrThrow(request, 'dataset labels')
                    resolve(Array.isArray(response?.labels) ? response.labels : [])
                } catch (error) {
                    reject(error)
                }
            } else {
                reject(new Error('Failed to fetch dataset labels'))
            }
        }
        request.onerror = () => reject(new Error('Request error'))
        request.send()
    })
}

export function getExpressions(teamId: number): Promise<Array<Expression>> {
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest()
        request.open('GET', withShareId(`/teams/${teamId}/expressions?json=true`))
        request.setRequestHeader('X-CSRF-Token', (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement).content)
        request.onload = () => {
            if (request.status === 200) {
                try {
                    resolve(parseJsonOrThrow(request, 'expressions'))
                } catch (error) {
                    reject(error)
                }
            } else {
                reject(new Error('Failed to fetch expressions'))
            }
        }
        request.onerror = () => {
            reject(new Error('Request error'))
        }
        request.send()
    })
}

export interface AnnotationClass {
    id: number
    name: string
    color: string
    order: number
}

export interface AnnotationRecord {
    id: number
    name: string
    team_id: number
    created_at: string
    updated_at: string
    classes?: AnnotationClass[] | null
    geojson_url?: string | null
}

export interface AnnotationMarkerRecord {
    id: number
    name: string
    center: [number, number]
    bbox: [number, number, number, number]
}

export function getAnnotations(teamId: number): Promise<Array<AnnotationRecord>> {
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest()
        request.open('GET', withShareId(`/teams/${teamId}/paintbrush?json=true`))
        request.setRequestHeader('X-CSRF-Token', (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement).content)
        request.onload = () => {
            if (request.status === 200) {
                try {
                    resolve(parseJsonOrThrow(request, 'annotations'))
                } catch (error) {
                    reject(error)
                }
            } else {
                reject(new Error('Failed to fetch annotations'))
            }
        }
        request.onerror = () => {
            reject(new Error('Request error'))
        }
        request.send()
    })
}

export function getAnnotationMarkers(teamId: number): Promise<Array<AnnotationMarkerRecord>> {
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest()
        request.open('GET', withShareId(`/teams/${teamId}/paintbrush/markers`))
        request.setRequestHeader('X-CSRF-Token', (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement).content)
        request.onload = () => {
            if (request.status === 200) {
                try {
                    resolve(parseJsonOrThrow(request, 'annotation markers'))
                } catch (error) {
                    reject(error)
                }
            } else {
                reject(new Error('Failed to fetch annotation markers'))
            }
        }
        request.onerror = () => {
            reject(new Error('Request error'))
        }
        request.send()
    })
}